/**
 * Captura la cámara del usuario y la transmite por WebRTC al scrapper.
 *
 * Al activarse (fase de captura): pide permiso de cámara, abre el WS de
 * signaling (`…/biometric/signal/cam`), negocia WebRTC y envía el track de
 * video. Devuelve el `MediaStream` local (para previsualizar) y el estado de la
 * conexión. Se limpia solo al desactivarse o desmontar.
 */

import { useEffect, useState } from 'react';
import { signalWsUrl } from './scrapperApi.js';

export type CameraState =
  | 'idle'
  | 'requesting' // pidiendo permiso de cámara
  | 'connecting' // negociando WebRTC
  | 'streaming' // transmitiendo al scrapper
  | 'denied' // permiso denegado
  | 'error';

export interface BiometricCameraState {
  stream: MediaStream | null;
  state: CameraState;
  errorMessage: string | null;
  /** Reintenta la captura (re-pide permiso y renegocia). */
  retry: () => void;
}

interface SignalMessage {
  type?: string;
  sdp?: string;
  candidate?: RTCIceCandidateInit;
}

const STUN: RTCConfiguration = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

export const useBiometricCamera = (
  scrapperBase: string,
  paymentId: string,
  active: boolean,
): BiometricCameraState => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [state, setState] = useState<CameraState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!active) {
      setState('idle');
      setStream(null);
      setErrorMessage(null);
      return;
    }

    let cancelled = false;
    let pc: RTCPeerConnection | null = null;
    let ws: WebSocket | null = null;
    let localStream: MediaStream | null = null;

    const cleanup = (): void => {
      if (ws) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
        ws = null;
      }
      if (pc) {
        try {
          pc.close();
        } catch {
          /* noop */
        }
        pc = null;
      }
      if (localStream) {
        localStream.getTracks().forEach((t) => t.stop());
        localStream = null;
      }
    };

    const fail = (kind: CameraState, msg: string): void => {
      if (cancelled) return;
      setState(kind);
      setErrorMessage(msg);
      cleanup();
    };

    const start = async (): Promise<void> => {
      setState('requesting');
      setErrorMessage(null);

      if (!navigator.mediaDevices?.getUserMedia) {
        fail('error', 'Tu navegador no soporta el acceso a la cámara.');
        return;
      }

      let ms: MediaStream;
      try {
        ms = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (err) {
        const name = err instanceof DOMException ? err.name : '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          fail('denied', 'Necesitamos permiso para usar tu cámara. Actívalo y reintenta.');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          fail('error', 'No encontramos una cámara disponible en tu dispositivo.');
        } else {
          fail('error', 'No pudimos acceder a tu cámara.');
        }
        return;
      }

      if (cancelled) {
        ms.getTracks().forEach((t) => t.stop());
        return;
      }
      localStream = ms;
      setStream(ms);

      setState('connecting');
      const peer = new RTCPeerConnection(STUN);
      pc = peer;
      ms.getTracks().forEach((t) => peer.addTrack(t, ms));

      peer.addEventListener('icecandidate', (e) => {
        if (e.candidate && ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ice', candidate: e.candidate.toJSON() }));
        }
      });
      peer.addEventListener('connectionstatechange', () => {
        if (cancelled) return;
        if (peer.connectionState === 'connected') setState('streaming');
        else if (peer.connectionState === 'failed')
          fail('error', 'Se perdió la conexión de video. Reintenta.');
      });

      const socket = new WebSocket(signalWsUrl(scrapperBase, paymentId));
      ws = socket;

      socket.addEventListener('message', (ev: MessageEvent) => {
        void (async () => {
          let m: SignalMessage;
          try {
            m = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as SignalMessage;
          } catch (err) {
            // No dejar la negociación WebRTC colgada en silencio (spinner infinito).
            console.warn('[lpay-widget] mensaje de señalización malformado, ignorado', err);
            return;
          }
          if (m.type === 'answer' && typeof m.sdp === 'string') {
            await peer.setRemoteDescription({ type: 'answer', sdp: m.sdp });
          } else if (m.type === 'ice' && m.candidate) {
            try {
              await peer.addIceCandidate(m.candidate);
            } catch {
              /* candidato tardío tras cierre: ignorar */
            }
          }
        })();
      });
      socket.addEventListener('error', () => {
        fail('error', 'No pudimos conectar con el servicio de verificación.');
      });

      const sendOffer = async (): Promise<void> => {
        if (cancelled) return;
        await peer.setLocalDescription(await peer.createOffer());
        const sdp = peer.localDescription?.sdp;
        if (sdp && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'offer', sdp }));
        }
      };
      if (socket.readyState === WebSocket.OPEN) void sendOffer();
      else socket.addEventListener('open', () => void sendOffer());
    };

    void start();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [scrapperBase, paymentId, active, nonce]);

  return { stream, state, errorMessage, retry: () => setNonce((n) => n + 1) };
};
